import { format, parseISO } from 'date-fns';

/**
 * Normalizes a database date string to a valid ISO-8601 format.
 *
 * Database timestamps often come in the format `2026-05-19 14:17:56.076 +0200`
 * (with a space between the date and time parts instead of the standard `T`).
 * Passing such strings directly to `new Date()` causes inconsistent parsing
 * across different browsers and timezones.
 *
 * This function replaces the first space (between date and time) with a `T`,
 * producing a spec-compliant ISO-8601 string that all browsers parse the same way.
 */
export const normalizeDbDateString = (dateStr: string): string =>
  dateStr.replace(/^(\d{4}-\d{2}-\d{2})\s/, '$1T');

interface DateDisplayOptions {
  showYear?: boolean;
  showMonth?: boolean;
  showDay?: boolean;
  showHour?: boolean;
  showMinute?: boolean;
  showSecond?: boolean;
}

export const isoDateToDisplayString = (
  isoDate: string,
  {
    showYear = true,
    showMonth = true,
    showDay = true,
    showHour = true,
    showMinute = true,
    showSecond = true,
  }: DateDisplayOptions = {},
) => {
  const dateTokens: string[] = [];
  if (showYear) dateTokens.push('yyyy');
  if (showMonth) dateTokens.push('MM');
  if (showDay) dateTokens.push('dd');

  const timeTokens: string[] = [];
  if (showHour) timeTokens.push('HH');
  if (showMinute) timeTokens.push('mm');
  if (showSecond) timeTokens.push('ss');

  let pattern = '';
  if (dateTokens.length) pattern += dateTokens.join('/');
  if (timeTokens.length) {
    if (pattern) pattern += ' ';
    pattern += timeTokens.join(':');
  }

  return format(parseISO(isoDate), pattern);
};
